import { useTranslation } from 'react-i18next'

import { TimeTrackingFilterDropdown } from './TimeTrackingFilterDropdown'
import { TimeTrackingTotals } from './TimeTrackingTotals'
import { TAB_CONFIG, TabViewType } from '../const'
import { OrderFilterDropdown } from '../tabs/myOrders/OrderFilterDropdown'
import {
  ClientOption,
  WeekRange,
  FilterValues,
  TimeTrackingTab,
  DisplayFormat,
  OrderScope,
} from '../types'
import { Icon } from '@/Atomic/Atoms/Icon'
import { ToggleButton } from '@/Atomic/Atoms/ToggleButton'
import { Button } from '@/Atomic/Molecules/Button'
import { DateRangeDropdown } from '@/Atomic/Molecules/DateRangeDropdown'
import { Toggle } from '@/Atomic/Molecules/Toggle'
import { CustomerWithOrders } from '@/Widgets/LogTimeDrawerWidget/types'

interface TimeTrackingControlBarProps {
  weekRange: WeekRange
  statusOptions: Array<{ label: string; value: string }>
  teamOptions: Array<{ label: string; value: string }>
  teamMemberOptions?: Array<{ label: string; value: string }>
  teamLeaderIds?: string[]
  customers?: CustomerWithOrders[]
  customerOptions?: ClientOption[]
  initialFilters?: FilterValues
  onWeekChange: (range: WeekRange) => void
  onNavigateWeek: (direction: 'prev' | 'next') => void
  onCurrentWeek: () => void
  onFilterChange: (filters: FilterValues) => void
  onTeamLeaderSelect?: (leaderId: string) => void
  onLogTime?: () => void
  onExportCSV?: () => void
  isExporting?: boolean
  onSummarize?: () => void
  currentTab: TimeTrackingTab
  displayFormat?: DisplayFormat
  onDisplayFormatChange?: (format: DisplayFormat) => void
  orderScope?: OrderScope
  onOrderScopeChange?: (scope: OrderScope) => void
  showWithRevenue?: boolean
  onShowWithRevenueChange?: (value: boolean) => void
  canFilterByTeam?: boolean
  totals?: {
    totalDuration: number
    billableDuration: number
    nonBillable: number
  }
}

export const TimeTrackingControlBar = ({
  weekRange,
  statusOptions,
  teamOptions,
  teamMemberOptions,
  teamLeaderIds,
  initialFilters,
  customerOptions,
  onWeekChange,
  onNavigateWeek,
  onCurrentWeek,
  onFilterChange,
  onTeamLeaderSelect,
  onLogTime,
  onExportCSV,
  isExporting,
  onSummarize,
  currentTab,
  displayFormat,
  onDisplayFormatChange,
  orderScope,
  onOrderScopeChange,
  showWithRevenue,
  onShowWithRevenueChange,
  canFilterByTeam = true,
  totals,
}: TimeTrackingControlBarProps) => {
  const { t } = useTranslation()
  const tabConfig = TAB_CONFIG[currentTab]

  return (
    <div className="time-tracking-controls">
      <div className="time-tracking-controls-left">
        <button
          type="button"
          className="time-tracking-nav-button"
          onClick={() => onNavigateWeek('prev')}
          aria-label={t('tooltip.previous')}
        >
          <Icon icon="chevron-left" />
        </button>
        <button
          type="button"
          className="time-tracking-nav-button"
          onClick={() => onNavigateWeek('next')}
          aria-label={t('common.next')}
        >
          <Icon icon="chevron-right" />
        </button>

        <DateRangeDropdown
          id="week-range"
          value={[weekRange.startDate, weekRange.endDate]}
          onChange={([from, to]) => {
            if (from && to) onWeekChange({ startDate: from, endDate: to })
          }}
          maxDays={tabConfig.maxDateRangeDays}
          format="MMM D"
          iconPosition="left"
        />

        <Button
          variant="outlined"
          title={
            tabConfig.viewType === TabViewType.MONTH
              ? t('timeTracking.currentMonth')
              : t('timeTracking.currentWeek')
          }
          size="sm"
          onClick={onCurrentWeek}
          className="current-week-btn"
        />

        {currentTab === TimeTrackingTab.MY_ORDERS ? (
          <OrderFilterDropdown
            initialFilters={initialFilters}
            customerOptions={customerOptions || []}
            onFilterChange={onFilterChange}
          />
        ) : (
          <TimeTrackingFilterDropdown
            statusOptions={statusOptions}
            teamOptions={teamOptions}
            teamMemberOptions={teamMemberOptions || []}
            teamLeaderIds={teamLeaderIds}
            initialFilters={initialFilters}
            onFilterChange={onFilterChange}
            onTeamLeaderSelect={onTeamLeaderSelect || (() => {})}
            currentTab={currentTab}
            canFilterByTeam={canFilterByTeam}
          />
        )}

        {tabConfig.toggle && displayFormat && onDisplayFormatChange && (
          <ToggleButton
            options={[
              {
                label: t(tabConfig.toggle[0].labelKey),
                value: tabConfig.toggle[0].value,
              },
              {
                label: t(tabConfig.toggle[1].labelKey),
                value: tabConfig.toggle[1].value,
              },
            ]}
            value={displayFormat}
            onChange={(value) => onDisplayFormatChange(value as DisplayFormat)}
            size="sm"
          />
        )}

        {currentTab === TimeTrackingTab.MY_ORDERS &&
          orderScope &&
          onOrderScopeChange && (
            <ToggleButton
              options={[
                {
                  label: t('timeTracking.billing.myOrders'),
                  value: 'myOrders',
                },
                {
                  label: t('timeTracking.billing.allOrders'),
                  value: 'allOrders',
                },
              ]}
              value={orderScope}
              onChange={(value) => onOrderScopeChange(value as OrderScope)}
              size="sm"
            />
          )}

        {currentTab === TimeTrackingTab.MY_ORDERS &&
          onShowWithRevenueChange && (
            <Toggle
              label={t('timeTracking.filterLabels.showWithRevenue')}
              checked={showWithRevenue ?? false}
              onChange={(e) => onShowWithRevenueChange(e.target.checked)}
            />
          )}
      </div>

      <div className="time-tracking-controls-right">
        {totals && <TimeTrackingTotals totals={totals} />}
        {onExportCSV && (
          <button
            className="export-csv-link"
            onClick={onExportCSV}
            disabled={isExporting}
          >
            {isExporting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
              />
            ) : (
              <Icon icon="download-circle" size="sm" />
            )}
            {t('timeTracking.billing.exportCSV')}
          </button>
        )}
        {onSummarize && (
          <Button
            icon="document-ai"
            variant="outlined"
            size="sm"
            onClick={onSummarize}
            aria-label={t('common.summarize')}
          >
            <span className="d-none d-md-inline">{t('common.summarize')}</span>
          </Button>
        )}
        {onLogTime && (
          <Button
            variant="primary"
            title={t('timeTracking.logTime')}
            icon="add"
            size="sm"
            onClick={onLogTime}
          />
        )}
      </div>
    </div>
  )
}
